import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { RootState } from "config/store";
import { COLORS } from "config/theme";
import { format } from "date-fns";
import { round } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CashRegister } from "shared/network/cash-register.api";
import { createReceipt } from "shared/network/receipt-summary.api";
import { BaseCurrency, ItemPriceHistory, ItemTaxHistory } from "shared/types";

type Props = {
  groupedItems: {
    tax: any;
    items: any;
    total: any;
  }[];
  isBlockDialogOpen: any;
  setIsBlockDialogOpen: any;
  currency: BaseCurrency;
  cashRegister: CashRegister | null;
};

const ReceiptDialog = ({
  groupedItems,
  isBlockDialogOpen,
  setIsBlockDialogOpen,
  currency,
  cashRegister,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const tenantId = useSelector(
    (state: RootState) => state.authentication?.selectedRelTenant?.tenant?.id,
  );
  const [receiptCreated, setReceiptCreated] = useState(false);

  async function createReceiptSubmit() {
    let submitData = {
      currency: currency,
      cashRegister: cashRegister,
      companySite: cashRegister?.companySite,
      netPriceA: 0,
      vatPercentageA: 0,
      vatPriceA: 0,
      grossPriceA: 0,
      netPriceB: 0,
      vatPercentageB: 0,
      vatPriceB: 0,
      grossPriceB: 0,
      netPriceC: 0,
      vatPercentageC: 0,
      vatPriceC: 0,
      grossPriceC: 0,
      netPriceE: 0,
      vatPercentageE: 0,
      vatPriceE: 0,
      grossPriceE: 0,
      releaseDate: format(new Date(), "yyyy-MM-dd"),
    };

    groupedItems?.forEach(item => {
      const gross = Number(item.total) || 0;
      const vatPercent = Number(item.tax) || 0;
      const net = vatPercent > 0 ? round(gross / (1 + vatPercent / 100), 2) : gross;
      const vat = round(gross - net, 2);

      if (vatPercent === 5) {
        submitData["netPriceA"] = net;
        submitData["vatPercentageA"] = vatPercent;
        submitData["vatPriceA"] = vat;
        submitData["grossPriceA"] = gross;
      }
      if (vatPercent === 18) {
        submitData["netPriceB"] = net;
        submitData["vatPercentageB"] = vatPercent;
        submitData["vatPriceB"] = vat;
        submitData["grossPriceB"] = gross;
      }
      if (vatPercent === 27) {
        submitData["netPriceC"] = net;
        submitData["vatPercentageC"] = vatPercent;
        submitData["vatPriceC"] = vat;
        submitData["grossPriceC"] = gross;
      }
      if (vatPercent === 0) {
        submitData["netPriceE"] = net;
        submitData["vatPercentageE"] = vatPercent;
        submitData["vatPriceE"] = vat;
        submitData["grossPriceE"] = gross;
      }
    });
    try {
      await createReceipt(submitData, tenantId);
      setReceiptCreated(true);
    } catch {}
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isBlockDialogOpen}
      onClose={() => setIsBlockDialogOpen(false)}
    >
      <DialogTitle>{t("order.buy.purchaseReceipt")}</DialogTitle>
      <DialogContent>
        {groupedItems.map(group => (
          <Card
            key={group.tax}
            style={{
              marginBottom: 16,
              padding: isSmallScreen ? 8 : 16,
            }}
          >
            <Typography
              style={{
                fontWeight: "bold",
                fontSize: isSmallScreen ? 14 : 16,
                marginBottom: 8,
                color: COLORS.main,
              }}
            >
              {t("order.buy.taxRate")}: {group.tax}%
            </Typography>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography style={{ fontWeight: "bold" }}>{t("order.buy.product")}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography style={{ fontWeight: "bold" }}>{t("order.buy.quantity")}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography style={{ fontWeight: "bold" }}>{t("order.buy.unitPrice")}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography style={{ fontWeight: "bold" }}>{t("order.buy.total")}</Typography>
                </Grid>
              </Grid>
              {group.items.map((item: any, index: number) => {
                const priceHistory = item.item.itemPriceHistories.find(
                  (price: ItemPriceHistory) =>
                    price.isActive && currency.id === price?.currency?.id,
                );
                const price = priceHistory?.price ? Number(priceHistory?.price) : 0;
                const tax =
                  (item.item?.itemTaxHistories?.find((tax: ItemTaxHistory) => tax.isActive)?.tax
                    ?.percent || 0) / 100 || 0;
                const grossPrice = round(price * (1 + tax), 2);

                return (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    style={{
                      borderBottom: "1px solid #e0e0e0",
                      paddingBottom: 8,
                      marginBottom: 8,
                    }}
                  >
                    <Grid item xs={12} sm={6}>
                      <Typography>{item.item.name}</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Typography>{item.quantity}</Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Typography>
                        {t("common:currency", { num: grossPrice, cur: currency?.postfix })}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Typography>
                        {t("common:currency", {
                          num: item.quantity * grossPrice,
                          cur: currency?.postfix,
                        })}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
            <Typography
              style={{
                fontWeight: "bold",
                fontSize: isSmallScreen ? 14 : 16, // Kisebb betűméret mobilon
                marginTop: 8,
                textAlign: "right",
              }}
            >
              {t("order.buy.total")}:{" "}
              {t("common:currency", { num: group.total, cur: currency?.postfix })}
            </Typography>
          </Card>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => setIsBlockDialogOpen(false)} color="primary">
          {t("common:button.close")}
        </Button>
        <Button variant="contained" color="primary" onClick={() => createReceiptSubmit()}>
          {t("selling.createReceipt")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptDialog;
